import { useState, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  Input,
  Textarea,
  Select,
  HStack,
  Text,
  FormErrorMessage,
  Stack,
  Heading,
} from "@chakra-ui/react";
import { useForm, useFieldArray, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Plus, Trash2 } from "lucide-react";
import MediaUpload from "src/features/jobs/media-upload";
import ResumeUpload, { ResumeData } from "src/features/jobs/resume-upload";
import { EducationLevel } from "src/__generated__/graphql";
import { useAuth0 } from "@auth0/auth0-react";
import { useLazyQuery, useQuery } from "@apollo/client";
import { isEqual } from "lodash";
import { logEvent } from "src/utlis/analytics";
import {
  JobApplicationFormData as FormData,
  jobApplicationFormSchema as formSchema,
  JobApplicationFormProps,
  tertiaryQualifications,
  educationLevels,
} from "src/features/jobs/constants";
import { convertDateForInput, convertMedia } from "src/features/jobs/utils";
import {
  GET_MY_PORTFOLIO_FOR_JOB_APPLICATION_PREFILL,
  GET_MY_JOB_APPLICATIONS,
} from "@/features/jobs/graphql";
import { debounce } from "src/utlis/helpers";

export default function LaborHackJobApplicationForm({
  onSubmit,
  onCancel,
  isSubmitting,
  jobId,
}: JobApplicationFormProps) {
  const { user, isLoading: authIsLoading } = useAuth0();

  // Store original form data after prefilling
  const [originalFormPrefilledData, setOriginalFormPrefilledData] =
    useState<FormData | null>(null);

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      educationItems: [],
      employmentHistoryItems: [],
      projectHistoryItems: [],
      certificationItems: [],
    },
  });

  const { data: lastJobApplicationData, loading: loadingLastJobApplication } =
    useQuery(GET_MY_JOB_APPLICATIONS, {
      variables: {
        limit: 1,
      },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        // Use the most recent application to prefill the form
        const lastApplication = data.allMyJobApplications?.[0];

        if (lastApplication) {
          populateFormFromLastApplication(lastApplication);
        } else {
          // No applications found, use portfolio
          getPortfolioData();
        }
      },
      onError: (error) => {
        console.error("Error fetching last job application:", error);
        // Fallback to portfolio
        getPortfolioData();
      },
    });

  const [getPortfolioData, { data: portfolioData, loading: loadingPortfolio }] =
    useLazyQuery(GET_MY_PORTFOLIO_FOR_JOB_APPLICATION_PREFILL, {
      onCompleted: (data) => {
        if (data.myPortfolio) {
          populateFormFromPortfolio(data.myPortfolio);
        }
      },
      onError: (error) => {
        console.error("Error fetching portfolio data:", error);
      },
    });

  /**
   * Function to populate form from last job application
   * @param lastApplication - The last job application data to populate the form from
   */
  const populateFormFromLastApplication = (
    lastApplication: NonNullable<
      typeof lastJobApplicationData
    >["allMyJobApplications"][0]
  ) => {
    if (!lastApplication) return;

    // Populate education items
    if (lastApplication.education && lastApplication.education.length > 0) {
      const educationItems = lastApplication.education.map((education) => ({
        schoolName: education.schoolName,
        fieldOfStudy: education.fieldOfStudy || "",
        qualification: education.qualification,
      }));
      setValue("educationItems", educationItems);
    }

    // Populate employment history items
    if (
      lastApplication.employmentHistory &&
      lastApplication.employmentHistory.length > 0
    ) {
      const employmentItems = lastApplication.employmentHistory.map(
        (employment) => ({
          companyName: employment.companyName,
          jobTitle: employment.jobTitle,
          description: employment.description,
          startedAt: convertDateForInput(employment.startedAt),
          endedAt: convertDateForInput(employment.endedAt),
          media: convertMedia(employment.media),
        })
      );
      setValue("employmentHistoryItems", employmentItems);
    }

    // Populate project history items
    if (
      lastApplication.projectHistory &&
      lastApplication.projectHistory.length > 0
    ) {
      const projectItems = lastApplication.projectHistory.map((project) => ({
        name: project.name,
        description: project.description,
        startedAt: convertDateForInput(project.startedAt),
        endedAt: convertDateForInput(project.endedAt),
        media: convertMedia(project.media),
      }));
      setValue("projectHistoryItems", projectItems);
    }

    // Populate certifications
    if (
      lastApplication.certifications &&
      lastApplication.certifications.length > 0
    ) {
      const certificationItems = lastApplication.certifications.map(
        (certification) => ({
          name: certification.name,
          issuerCertificationId: certification.issuerCertificationId || "",
          issuedBy: certification.issuedBy,
          issuedAt: convertDateForInput(certification.issuedAt),
          url: certification.url || "",
        })
      );
      setValue("certificationItems", certificationItems);
    }

    logEvent("laborhack_application_form_prefilled", {
      jobAdId: jobId,
      userId: user?.id,
      source: "last_job_application",
    });

    // set the original form prefilled data
    const currentFormData = normalizeFormData(watch());
    setOriginalFormPrefilledData(structuredClone(currentFormData));
  };

  /**
   * Function to populate form from portfolio data
   * @param portfolio - The portfolio data to populate the form from
   */
  const populateFormFromPortfolio = (
    portfolio: NonNullable<typeof portfolioData>["myPortfolio"]
  ) => {
    if (!portfolio) return;

    // Check if there's at least one work history or employment history item
    const hasWorkHistory =
      portfolio.workHistory && portfolio.workHistory.length > 0;
    const hasEmploymentHistory =
      portfolio.employmentHistory && portfolio.employmentHistory.length > 0;

    if (!hasWorkHistory && !hasEmploymentHistory) {
      return; // Don't prefill if no work/employment history
    }

    // Populate education items from portfolio
    if (portfolio.educationEntries && portfolio.educationEntries.length > 0) {
      const educationItems = portfolio.educationEntries.map((education) => ({
        schoolName: education.schoolName,
        fieldOfStudy: education.fieldOfStudy || "",
        qualification: education.qualification,
      }));
      setValue("educationItems", educationItems);
    }

    // Populate employment history items from portfolio
    if (hasEmploymentHistory) {
      const employmentItems = portfolio.employmentHistory.map((employment) => ({
        companyName: employment.companyName,
        jobTitle: employment.jobTitle,
        description: employment.description,
        startedAt: convertDateForInput(employment.startedAt),
        endedAt: convertDateForInput(employment.endedAt),
        media: convertMedia(
          employment.images
            // filter out images that don't have a key, mime type, original url, and thumbnail url
            .filter(
              (image) =>
                image.key &&
                image.mimeType &&
                image.originalUrl &&
                image.thumbnailUrl
            )
            .map((image) => ({
              key: image.key!,
              mimeType: image.mimeType,
              originalUrl: image.originalUrl!,
              thumbnailUrl: image.thumbnailUrl!,
            }))
        ),
      }));
      setValue("employmentHistoryItems", employmentItems);
    }

    // Populate project history items from portfolio work history
    if (hasWorkHistory) {
      const projectItems = portfolio.workHistory.map((work) => ({
        name: work.name,
        description: work.description,
        startedAt: convertDateForInput(work.startedAt),
        endedAt: convertDateForInput(work.endedAt),
        media: convertMedia(
          work.images
            // filter out images that don't have a key, mime type, original url, and thumbnail url
            .filter(
              (image) =>
                image.key &&
                image.mimeType &&
                image.originalUrl &&
                image.thumbnailUrl
            )
            .map((image) => ({
              key: image.key!,
              mimeType: image.mimeType,
              originalUrl: image.originalUrl!,
              thumbnailUrl: image.thumbnailUrl!,
            }))
        ),
      }));
      setValue("projectHistoryItems", projectItems);
    }

    // Populate certifications from portfolio
    if (portfolio.certifications && portfolio.certifications.length > 0) {
      const certificationItems = portfolio.certifications.map(
        (certification) => ({
          name: certification.name,
          issuerCertificationId: certification.issuerCertificationId || "",
          issuedBy: certification.issuedBy,
          issuedAt: convertDateForInput(certification.issuedAt),
          url: certification.url || "",
        })
      );
      setValue("certificationItems", certificationItems);
    }

    logEvent("laborhack_application_form_prefilled", {
      jobAdId: jobId,
      userId: user?.id,
      source: "portfolio",
    });

    // set the original form prefilled data
    const currentFormData = normalizeFormData(watch());
    setOriginalFormPrefilledData(structuredClone(currentFormData));
  };

  /**
   * Function to populate form from parsed resume data
   * @param resumeData - The parsed resume data
   */
  const populateFormFromResume = (resumeData: ResumeData) => {
    const educationItems = resumeData.educationItems.map((item) => ({
      ...item,
      qualification: item.qualification as EducationLevel,
    }));
    setValue("educationItems", educationItems);

    const employmentItems = resumeData.employmentHistoryItems.map((item) => ({
      ...item,
      media: [],
    }));
    setValue("employmentHistoryItems", employmentItems);

    const projectItems = resumeData.projectHistoryItems.map((item) => ({
      ...item,
      endedAt: item.endedAt || "",
      media: [],
    }));
    setValue("projectHistoryItems", projectItems);

    const certificationItems = resumeData.certificationItems.map((item) => ({
      ...item,
      url: item.url || "",
      issuerCertificationId: item.issuerCertificationId || "",
    }));
    setValue("certificationItems", certificationItems);

    logEvent("laborhack_application_form_prefilled", {
      jobAdId: jobId,
      userId: user?.id,
      source: "resume_upload",
    });

    const currentFormData = normalizeFormData(watch());
    setOriginalFormPrefilledData(structuredClone(currentFormData));
  };

  /**
   * Function to normalize form data by converting undefined to empty strings
   * this is to avoid misreading data when comparing the original form prefilled data with the current form data
   * @param data - The form data to normalize
   * @returns The normalized form data
   */
  const normalizeFormData = useCallback((data: FormData): FormData => {
    return {
      ...data,
      educationItems: data.educationItems.map((item) => ({
        ...item,
        fieldOfStudy: item.fieldOfStudy || "",
      })),
      employmentHistoryItems: data.employmentHistoryItems.map((item) => ({
        ...item,
        endedAt: item.endedAt || "",
      })),
      projectHistoryItems: data.projectHistoryItems.map((item) => ({
        ...item,
        startedAt: item.startedAt || "",
      })),
      certificationItems: data.certificationItems.map((item) => ({
        ...item,
        issuerCertificationId: item.issuerCertificationId || "",
        url: item.url || "",
      })),
    };
  }, []);

  /**
   * Function to determine which source was used for prefilling the form
   * @returns The source of prefilling data
   */
  const getPrefillSource = useCallback(() => {
    const lastApplication = lastJobApplicationData?.allMyJobApplications?.[0];

    if (lastApplication) {
      return "job_application";
    } else if (portfolioData?.myPortfolio) {
      return "portfolio";
    }

    return "none";
  }, [lastJobApplicationData, portfolioData]);

  // Function to check if prefilled form has been modified
  const hasPrefilledFormChanged = useCallback(() => {
    if (!originalFormPrefilledData) return false;
    const currentData = normalizeFormData(watch());
    const originalData = normalizeFormData(originalFormPrefilledData);
    return !isEqual(originalData, currentData);
  }, [originalFormPrefilledData, watch, normalizeFormData]);

  const debouncedLogEvent = debounce(
    (event: string, data: Record<string, any>) => {
      logEvent(event, data);
    },
    1000
  );

  const {
    fields: educationFields,
    append: appendEducation,
    remove: removeEducation,
  } = useFieldArray({
    control,
    name: "educationItems",
  });

  const {
    fields: projectFields,
    append: appendProject,
    remove: removeProject,
  } = useFieldArray({
    control,
    name: "projectHistoryItems",
  });

  const {
    fields: employmentFields,
    append: appendEmployment,
    remove: removeEmployment,
  } = useFieldArray({
    control,
    name: "employmentHistoryItems",
  });

  const {
    fields: certificationFields,
    append: appendCertification,
    remove: removeCertification,
  } = useFieldArray({
    control,
    name: "certificationItems",
  });

  const convertToISOString = (dateString: string): string => {
    if (!dateString) return dateString;
    // Create a date object and convert to ISO string
    const date = new Date(dateString);
    return date.toISOString();
  };

  const onFormSubmit = (data: FormData) => {
    // Transform the data to match the GraphQL mutation structure
    // Note: Personal info (name, email, phone) is not included since pro is authenticated
    const transformedData = {
      educationItems: data.educationItems,
      employmentHistoryItems: data.employmentHistoryItems.map((item) => ({
        ...item,
        startedAt: convertToISOString(item.startedAt),
        endedAt: !!item.endedAt ? convertToISOString(item.endedAt) : undefined,
      })),
      projectHistoryItems: data.projectHistoryItems.map((item) => ({
        ...item,
        startedAt: !!item.startedAt
          ? convertToISOString(item.startedAt)
          : undefined,
        endedAt: convertToISOString(item.endedAt),
      })),
      certificationItems: data.certificationItems.map((item) => ({
        ...item,
        issuedAt: convertToISOString(item.issuedAt),
      })),
    };

    onSubmit(transformedData);

    if (hasPrefilledFormChanged()) {
      logEvent("prefilled_laborhack_form_modified", {
        jobAdId: jobId,
        userId: user?.id,
        prefilledFrom: getPrefillSource(),
      });
    } else if (!hasPrefilledFormChanged()) {
      logEvent("prefilled_laborhack_form_not_modified", {
        jobAdId: jobId,
        userId: user?.id,
        prefilledFrom: getPrefillSource(),
      });
    }
  };

  if (authIsLoading || loadingLastJobApplication || loadingPortfolio) {
    return <div>Loading...</div>;
  }

  return (
		<Box as="form" onSubmit={handleSubmit(onFormSubmit)} maxW="4xl" mx="auto">
			<Stack spacing={8}>
				<ResumeUpload onUploadSuccess={populateFormFromResume} jobId={jobId} />

				{/* Education Section */}
				<Box py={8} borderBottom="1px" borderColor="#E7E7E7">
					<HStack justify="space-between" mb={6}>
						<Heading
							as="h2"
							fontSize={{ base: "xl", lg: "2xl" }}
							fontWeight="medium"
							color="#121212"
						>
							Education
						</Heading>
						<Button
							leftIcon={<Plus className="h-4 w-4" />}
							size="md"
							variant="outline"
							rounded={0}
							colorScheme="prussianBlue"
							onClick={() => {
								appendEducation({
									schoolName: "",
									fieldOfStudy: "",
									qualification: EducationLevel.Ssce,
								});
								logEvent("laborhack_education_added", {
									jobAdId: jobId,
									userId: user?.id,
								});
							}}
						>
							Add
						</Button>
					</HStack>

					<Stack spacing={6}>
						{educationFields.map((field, index) => (
							<Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
								<HStack justify="space-between" mb={4}>
									<Text fontWeight="medium" fontSize="lg">
										Education {index + 1}
									</Text>
									<Button
										size="sm"
										variant="ghost"
										colorScheme="red"
										onClick={() => {
											removeEducation(index);
											logEvent("laborhack_education_removed", {
												jobAdId: jobId,
												userId: user?.id,
											});
										}}
										leftIcon={<Trash2 className="h-4 w-4" />}
									>
										Remove
									</Button>
								</HStack>

								<Stack spacing={4}>
									<FormControl
										isInvalid={!!errors.educationItems?.[index]?.schoolName}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`schoolName-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												School name
											</label>
											<Input
												{...register(`educationItems.${index}.schoolName`)}
												placeholder="Enter school name"
												variant="outline"
												rounded={0}
												border="1px solid #D7D7D7"
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												bg="white"
												onChange={(e) => {
													register(
														`educationItems.${index}.schoolName`
													).onChange(e);
													if (e.target.value.trim()) {
														debouncedLogEvent(
															"laborhack_education_schoolName_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											/>
											<FormErrorMessage>
												{errors.educationItems?.[index]?.schoolName?.message}
											</FormErrorMessage>
										</div>
									</FormControl>

									<FormControl
										isInvalid={!!errors.educationItems?.[index]?.qualification}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`qualification-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Qualification
											</label>
											<Select
												id={`qualification-${index}`}
												{...register(`educationItems.${index}.qualification`)}
												placeholder="Select qualification"
												variant="outline"
												rounded={0}
												border="1px solid #D7D7D7"
												size={{ base: "md", lg: "lg" }}
												h={{ base: 10, lg: 16 }}
												bg="white"
												onChange={(e) => {
													register(
														`educationItems.${index}.qualification`
													).onChange(e);
													if (e.target.value) {
														debouncedLogEvent(
															"laborhack_education_qualification_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											>
												{educationLevels.map((level) => (
													<option key={level.value} value={level.value}>
														{level.label}
													</option>
												))}
											</Select>
											<FormErrorMessage>
												{errors.educationItems?.[index]?.qualification?.message}
											</FormErrorMessage>
										</div>
									</FormControl>

									{watch(`educationItems.${index}.qualification`) &&
										tertiaryQualifications.includes(
											watch(
												`educationItems.${index}.qualification`
											) as EducationLevel
										) && (
											<FormControl
												isInvalid={
													!!errors.educationItems?.[index]?.fieldOfStudy
												}
											>
												<div className="flex flex-col gap-4">
													<label
														htmlFor={`fieldOfStudy-${index}`}
														className="text-md lg:text-xl text-[#212121] font-medium"
													>
														Field of study
													</label>
													<Input
														{...register(
															`educationItems.${index}.fieldOfStudy`
														)}
														placeholder="e.g Electrical Engineering"
														variant="outline"
														rounded={0}
														border="1px solid #D7D7D7"
														py={{ base: 5, lg: 8 }}
														px={{ base: 3, lg: 5 }}
														bg="white"
														onChange={(e) => {
															register(
																`educationItems.${index}.fieldOfStudy`
															).onChange(e);
															if (e.target.value.trim()) {
																debouncedLogEvent(
																	"laborhack_education_fieldOfStudy_changed",
																	{
																		jobAdId: jobId,
																		userId: user?.id,
																	}
																);
															}
														}}
													/>
													<FormErrorMessage>
														{
															errors.educationItems?.[index]?.fieldOfStudy
																?.message
														}
													</FormErrorMessage>
												</div>
											</FormControl>
										)}
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
				{/* Employment History Section */}
				<Box py={8} borderBottom="1px" borderColor="#E7E7E7">
					<HStack justify="space-between" mb={6}>
						<Heading
							as="h2"
							fontSize={{ base: "xl", lg: "2xl" }}
							fontWeight="medium"
							color="#121212"
						>
							Employment history
						</Heading>
						<Button
							leftIcon={<Plus className="h-4 w-4" />}
							size="md"
							variant="outline"
							rounded={0}
							colorScheme="prussianBlue"
							onClick={() => {
								appendEmployment({
									companyName: "",
									jobTitle: "",
									description: "",
									startedAt: "",
									endedAt: "",
									media: [],
								});
								logEvent("laborhack_employment_added", {
									jobAdId: jobId,
									userId: user?.id,
								});
							}}
						>
							Add
						</Button>
					</HStack>

					<Stack spacing={6}>
						{employmentFields.map((field, index) => (
							<Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
								<HStack justify="space-between" mb={4}>
									<Text fontWeight="medium" fontSize="lg">
										Employment {index + 1}
									</Text>
									<Button
										size="sm"
										variant="ghost"
										colorScheme="red"
										onClick={() => {
											removeEmployment(index);
											logEvent("laborhack_employment_removed", {
												jobAdId: jobId,
												userId: user?.id,
											});
										}}
										leftIcon={<Trash2 className="h-4 w-4" />}
									>
										Remove
									</Button>
								</HStack>

								<Stack spacing={4}>
									<HStack spacing={4}>
										<FormControl
											isInvalid={
												!!errors.employmentHistoryItems?.[index]?.companyName
											}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`companyName-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Company name
												</label>
												<Input
													{...register(
														`employmentHistoryItems.${index}.companyName`
													)}
													placeholder="LaborHack"
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
													onChange={(e) => {
														register(
															`employmentHistoryItems.${index}.companyName`
														).onChange(e);
														if (e.target.value.trim()) {
															debouncedLogEvent(
																"laborhack_employment_companyName_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
												/>
												<FormErrorMessage>
													{
														errors.employmentHistoryItems?.[index]?.companyName
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>

										<FormControl
											isInvalid={
												!!errors.employmentHistoryItems?.[index]?.jobTitle
											}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`jobTitle-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Job title
												</label>
												<Input
													{...register(
														`employmentHistoryItems.${index}.jobTitle`
													)}
													placeholder="e.g Site Engineer"
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
													onChange={(e) => {
														register(
															`employmentHistoryItems.${index}.jobTitle`
														).onChange(e);
														if (e.target.value.trim()) {
															debouncedLogEvent(
																"laborhack_employment_jobTitle_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
												/>
												<FormErrorMessage>
													{
														errors.employmentHistoryItems?.[index]?.jobTitle
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>
									</HStack>

									<FormControl
										isInvalid={
											!!errors.employmentHistoryItems?.[index]?.description
										}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`jobDescription-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Job description
											</label>
											<Textarea
												{...register(
													`employmentHistoryItems.${index}.description`
												)}
												placeholder="Describe your role and responsibilities"
												rows={3}
												rounded={0}
												border="1px solid #D7D7D7"
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												bg="white"
												onChange={(e) => {
													register(
														`employmentHistoryItems.${index}.description`
													).onChange(e);
													if (e.target.value.trim()) {
														debouncedLogEvent(
															"laborhack_employment_description_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											/>
											<FormErrorMessage>
												{
													errors.employmentHistoryItems?.[index]?.description
														?.message
												}
											</FormErrorMessage>
										</div>
									</FormControl>

									<Stack direction={{ base: "column", lg: "row" }} spacing={4}>
										<FormControl
											isInvalid={
												!!errors.employmentHistoryItems?.[index]?.startedAt
											}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`startDate-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Start date
												</label>
												<Input
													type="date"
													{...register(
														`employmentHistoryItems.${index}.startedAt`
													)}
													onChange={(e) => {
														register(
															`employmentHistoryItems.${index}.startedAt`
														).onChange(e);
														if (e.target.value) {
															debouncedLogEvent(
																"laborhack_employment_startDate_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
												<FormErrorMessage>
													{
														errors.employmentHistoryItems?.[index]?.startedAt
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>

										<FormControl>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`endDate-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													End date (optional)
												</label>
												<Input
													type="date"
													{...register(
														`employmentHistoryItems.${index}.endedAt`
													)}
													onChange={(e) => {
														register(
															`employmentHistoryItems.${index}.endedAt`
														).onChange(e);
														if (e.target.value) {
															debouncedLogEvent(
																"laborhack_employment_endDate_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
											</div>
										</FormControl>
									</Stack>

									<FormControl>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`images-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Images (optional)
											</label>
											<Controller
												name={`employmentHistoryItems.${index}.media`}
												control={control}
												render={({ field }) => (
													<MediaUpload
														media={field.value || []}
														onMediaChange={(e) => {
															field.onChange(e);
															if (e.length > 0) {
																debouncedLogEvent(
																	"laborhack_employment_images_changed",
																	{
																		jobAdId: jobId,
																		userId: user?.id,
																	}
																);
															}
														}}
														disabled={isSubmitting}
													/>
												)}
											/>
										</div>
									</FormControl>
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
				{/* Project History Section */}
				<Box py={8} borderBottom="1px" borderColor="#E7E7E7">
					<HStack justify="space-between" mb={6}>
						<Heading
							as="h2"
							fontSize={{ base: "xl", lg: "2xl" }}
							fontWeight="medium"
							color="#121212"
						>
							Project history
						</Heading>
						<Button
							leftIcon={<Plus className="h-4 w-4" />}
							size="md"
							variant="outline"
							rounded={0}
							colorScheme="prussianBlue"
							onClick={() => {
								appendProject({
									name: "",
									description: "",
									startedAt: "",
									endedAt: "",
									media: [],
								});
								logEvent("laborhack_project_added", {
									jobAdId: jobId,
									userId: user?.id,
								});
							}}
						>
							Add
						</Button>
					</HStack>

					<Stack spacing={6}>
						{projectFields.map((field, index) => (
							<Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
								<HStack justify="space-between" mb={4}>
									<Text fontWeight="medium" fontSize="lg">
										Project {index + 1}
									</Text>
									<Button
										size="sm"
										variant="ghost"
										colorScheme="red"
										onClick={() => {
											removeProject(index);
											logEvent("laborhack_project_removed", {
												jobAdId: jobId,
												userId: user?.id,
											});
										}}
										leftIcon={<Trash2 className="h-4 w-4" />}
									>
										Remove
									</Button>
								</HStack>

								<Stack spacing={4}>
									<FormControl
										isInvalid={!!errors.projectHistoryItems?.[index]?.name}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`projectName-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Project name
											</label>
											<Input
												{...register(`projectHistoryItems.${index}.name`)}
												placeholder="e.g 4 bedroom apartment solar installation"
												variant="outline"
												rounded={0}
												border="1px solid #D7D7D7"
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												bg="white"
												onChange={(e) => {
													register(
														`projectHistoryItems.${index}.name`
													).onChange(e);
													if (e.target.value.trim()) {
														debouncedLogEvent(
															"laborhack_project_name_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											/>
											<FormErrorMessage>
												{errors.projectHistoryItems?.[index]?.name?.message}
											</FormErrorMessage>
										</div>
									</FormControl>

									<FormControl
										isInvalid={
											!!errors.projectHistoryItems?.[index]?.description
										}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`projectDescription-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Project description
											</label>
											<Textarea
												{...register(
													`projectHistoryItems.${index}.description`
												)}
												placeholder="Describe the project and your role"
												rows={3}
												rounded={0}
												border="1px solid #D7D7D7"
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												bg="white"
												onChange={(e) => {
													register(
														`projectHistoryItems.${index}.description`
													).onChange(e);
													if (e.target.value.trim()) {
														debouncedLogEvent(
															"laborhack_project_description_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											/>
											<FormErrorMessage>
												{
													errors.projectHistoryItems?.[index]?.description
														?.message
												}
											</FormErrorMessage>
										</div>
									</FormControl>

									<Stack direction={{ base: "column", lg: "row" }} spacing={4}>
										<FormControl>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`projectStartDate-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Start date (optional)
												</label>
												<Input
													type="date"
													{...register(
														`projectHistoryItems.${index}.startedAt`
													)}
													onChange={(e) => {
														register(
															`projectHistoryItems.${index}.startedAt`
														).onChange(e);
														if (e.target.value) {
															debouncedLogEvent(
																"laborhack_project_startDate_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
											</div>
										</FormControl>

										<FormControl
											isInvalid={!!errors.projectHistoryItems?.[index]?.endedAt}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`projectEndDate-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													End date
												</label>
												<Input
													type="date"
													{...register(`projectHistoryItems.${index}.endedAt`)}
													onChange={(e) => {
														register(
															`projectHistoryItems.${index}.endedAt`
														).onChange(e);
														if (e.target.value) {
															debouncedLogEvent(
																"laborhack_project_endDate_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
												<FormErrorMessage>
													{
														errors.projectHistoryItems?.[index]?.endedAt
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>
									</Stack>

									<FormControl>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`projectImages-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Images (optional)
											</label>
											<Controller
												name={`projectHistoryItems.${index}.media`}
												control={control}
												render={({ field }) => (
													<MediaUpload
														media={field.value || []}
														onMediaChange={(e) => {
															field.onChange(e);
															if (e.length > 0) {
																debouncedLogEvent(
																	"laborhack_project_images_changed",
																	{
																		jobAdId: jobId,
																		userId: user?.id,
																	}
																);
															}
														}}
														disabled={isSubmitting}
													/>
												)}
											/>
										</div>
									</FormControl>
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>
				{/* Certifications Section */}
				<Box py={8} borderBottom="1px" borderColor="#E7E7E7">
					<HStack justify="space-between" mb={6}>
						<Heading
							as="h2"
							fontSize={{ base: "xl", lg: "2xl" }}
							fontWeight="medium"
							color="#121212"
						>
							Certifications
						</Heading>
						<Button
							leftIcon={<Plus className="h-4 w-4" />}
							size="md"
							variant="outline"
							rounded={0}
							colorScheme="prussianBlue"
							onClick={() => {
								appendCertification({
									name: "",
									issuerCertificationId: "",
									issuedBy: "",
									issuedAt: "",
									url: "",
								});
								logEvent("laborhack_certification_added", {
									jobAdId: jobId,
									userId: user?.id,
								});
							}}
						>
							Add
						</Button>
					</HStack>

					<Stack spacing={6}>
						{certificationFields.map((field, index) => (
							<Box key={field.id} p={6} border="1px" borderColor="#C9C9C9">
								<HStack justify="space-between" mb={4}>
									<Text fontWeight="medium" fontSize="lg">
										Certification {index + 1}
									</Text>
									<Button
										size="sm"
										variant="ghost"
										colorScheme="red"
										onClick={() => {
											removeCertification(index);
											logEvent("laborhack_certification_removed", {
												jobAdId: jobId,
												userId: user?.id,
											});
										}}
										leftIcon={<Trash2 className="h-4 w-4" />}
									>
										Remove
									</Button>
								</HStack>

								<Stack spacing={4}>
									<FormControl
										isInvalid={!!errors.certificationItems?.[index]?.name}
									>
										<div className="flex flex-col gap-4">
											<label
												htmlFor={`certificationName-${index}`}
												className="text-md lg:text-xl text-[#212121] font-medium"
											>
												Certification name
											</label>
											<Input
												{...register(`certificationItems.${index}.name`)}
												placeholder="e.g UK ECITB Health and Safety"
												variant="outline"
												rounded={0}
												border="1px solid #D7D7D7"
												py={{ base: 5, lg: 8 }}
												px={{ base: 3, lg: 5 }}
												bg="white"
												onChange={(e) => {
													register(`certificationItems.${index}.name`).onChange(
														e
													);
													if (e.target.value.trim()) {
														debouncedLogEvent(
															"laborhack_certification_name_changed",
															{
																jobAdId: jobId,
																userId: user?.id,
															}
														);
													}
												}}
											/>
											<FormErrorMessage>
												{errors.certificationItems?.[index]?.name?.message}
											</FormErrorMessage>
										</div>
									</FormControl>

									<HStack spacing={4}>
										<FormControl
											isInvalid={!!errors.certificationItems?.[index]?.issuedBy}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`issuedBy-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Issued by
												</label>
												<Input
													{...register(`certificationItems.${index}.issuedBy`)}
													placeholder="e.g UK ECITB"
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
													onChange={(e) => {
														register(
															`certificationItems.${index}.issuedBy`
														).onChange(e);
														if (e.target.value.trim()) {
															debouncedLogEvent(
																"laborhack_certification_issuedBy_changed",
																{
																	jobAdId: jobId,
																	userId: user?.id,
																}
															);
														}
													}}
												/>
												<FormErrorMessage>
													{
														errors.certificationItems?.[index]?.issuedBy
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>

										<FormControl
											isInvalid={!!errors.certificationItems?.[index]?.issuedAt}
										>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`issuedAt-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Issue date
												</label>
												<Input
													type="date"
													{...register(`certificationItems.${index}.issuedAt`)}
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
												<FormErrorMessage>
													{
														errors.certificationItems?.[index]?.issuedAt
															?.message
													}
												</FormErrorMessage>
											</div>
										</FormControl>
									</HStack>

									<HStack spacing={4}>
										<FormControl>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`certificationId-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Certification ID (optional)
												</label>
												<Input
													{...register(
														`certificationItems.${index}.issuerCertificationId`
													)}
													placeholder="e.g 1234567890"
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
											</div>
										</FormControl>

										<FormControl>
											<div className="flex flex-col gap-4">
												<label
													htmlFor={`certificationUrl-${index}`}
													className="text-md lg:text-xl text-[#212121] font-medium"
												>
													Certificate URL (optional)
												</label>
												<Input
													type="url"
													{...register(`certificationItems.${index}.url`)}
													placeholder="Enter certificate URL"
													variant="outline"
													rounded={0}
													border="1px solid #D7D7D7"
													py={{ base: 5, lg: 8 }}
													px={{ base: 3, lg: 5 }}
													bg="white"
												/>
											</div>
										</FormControl>
									</HStack>
								</Stack>
							</Box>
						))}
					</Stack>
				</Box>

				{/* Form Actions */}
				<Box
					py={{ base: 4, lg: 6 }}
					borderTop="1px"
					borderColor="#E7E7E7"
					display="flex"
					justifyContent="space-between"
					alignItems="center"
				>
					<Button
						variant="outline"
						onClick={() => {
							onCancel();
							logEvent("laborhack_application_cancelled", {
								jobAdId: jobId,
								userId: user?.id,
							});
						}}
						isDisabled={isSubmitting}
						rounded={0}
						px={8}
						py={6}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						colorScheme="brand"
						isLoading={isSubmitting}
						loadingText="Submitting application..."
						isDisabled={isSubmitting}
						rounded={0}
						px={8}
						py={6}
					>
						Submit application
					</Button>
				</Box>
			</Stack>
		</Box>
	);
}
